import { PinwheelExperienceInput } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useRouter,
} from 'expo-router';
import {
  DD_PROMPT_MAX_ADVANCE_LIMIT,
  DD_PROMPT_MIN_DIRECT_DEPOSIT_AMOUNT,
} from 'features/advance/constants/constants';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { Tag, TagType } from 'shared/components/vxl/Tag/Tag';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useDirectDepositLoadingComponent } from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

enum DirectDepositOption {
  WITH_DD = 'with_dd',
  WITHOUT_DD = 'without_dd',
}

const DDBorrowMore = () => {
  const router = useRouter();
  const [selectedOption, setSelectedOption] = useState<DirectDepositOption>(
    DirectDepositOption.WITH_DD,
  );
  const { bottom } = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'ddBorrowMore',
  });
  const dismissToTab = useDismissToRootTab();
  const goHome = () => dismissToTab(Tabs.HOME);

  const {
    shouldRedirectToDDManagementPage,
    shouldLaunchPinwheelSDK,
    isLoading: pinwheelFlowDecisionLoading,
    pinwheelFlowPageError,
    handleDdManagementLinkTokenFailure,
  } = useDirectDepositManagementFlow({
    pause: false,
  });

  const { initiatePinwheel, isPinwheelLoading: isPinwheelSdkLoading } =
    usePinwheel();

  const { analyticsEvents } = useAnalyticsConfiguration();
  const hasFiredScreenEvent = useRef(false);

  useEffect(() => {
    if (!pinwheelFlowDecisionLoading && !hasFiredScreenEvent.current) {
      hasFiredScreenEvent.current = true;
      void analyticsEvents.logScreenEventFromConfig('dd-borrow-more', {
        screen_variant: 'decision_first',
        pinwheel_error: String(!!pinwheelFlowPageError),
      });
    }
  }, [pinwheelFlowDecisionLoading, pinwheelFlowPageError, analyticsEvents]);

  const { skipIntroScreen } = useDirectDepositLoadingComponent();

  useEffect(() => {
    if (
      pinwheelFlowPageError &&
      selectedOption !== DirectDepositOption.WITHOUT_DD
    ) {
      setSelectedOption(DirectDepositOption.WITHOUT_DD);
    }
  }, [pinwheelFlowPageError, selectedOption]);

  if (shouldRedirectToDDManagementPage) {
    return <Redirect href="/varo-advance/link-account" />;
  }

  const handleGetAdvance = () => {
    if (selectedOption === DirectDepositOption.WITHOUT_DD) {
      return router.replace({
        pathname: '/varo-advance/link-account',
        params: { skipDdPrompt: 'true' },
      });
    }

    const outcomeCallback = shouldLaunchPinwheelSDK
      ? () => {
          router.replace('/varo-advance/link-account');
        }
      : goHome;

    initiatePinwheel({
      onOutcome: outcomeCallback,
      ...(shouldLaunchPinwheelSDK && {
        onLinkTokenError: handleDdManagementLinkTokenFailure,
        input: {
          experience: PinwheelExperienceInput.DirectDepositSwitch,
          skipIntroScreen,
        },
        launchBehavior: skipIntroScreen
          ? { delayAfterTokenMs: 3000 }
          : undefined,
      }),
    });
  };

  const isWithDDSelected = selectedOption === DirectDepositOption.WITH_DD;
  const isWithoutDDSelected = selectedOption === DirectDepositOption.WITHOUT_DD;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={() => {
                if (router.canGoBack()) {
                  router.back();
                  return;
                }
                router.dismissTo('/');
              }}
            />
          ),
        }}
      />

      <ScreenTransitionOverlay
        isLoading={isPinwheelSdkLoading}
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={false}
            isPinwheelLoading={isPinwheelSdkLoading}
          />
        }
        ErrorComponent={null}
      />

      <ScrollView contentContainerStyle={styles.scrollContent} bounces={false}>
        <View style={styles.headerSection}>
          <TitleXLText numberOfLines={0}>
            {translatedText('headline')}
          </TitleXLText>
          <BodyBaseText numberOfLines={0}>
            {translatedText('body')}
          </BodyBaseText>
        </View>

        <View style={styles.radiosSection} accessibilityRole="radiogroup">
          {!pinwheelFlowPageError ? (
            <Pressable
              style={[
                styles.radioCard,
                isWithDDSelected
                  ? styles.radioCardSelected
                  : styles.radioCardUnselected,
              ]}
              onPress={() => setSelectedOption(DirectDepositOption.WITH_DD)}
              accessibilityRole="radio"
              accessibilityState={{ checked: isWithDDSelected }}
              accessibilityLabel={translatedText('withDD.title')}
            >
              <View style={styles.radioCardContent}>
                <Tag
                  text={translatedText('withDD.tag')}
                  type={TagType.INFO_EMPHASIZED}
                  iconKey="SPARKLE_FILLED"
                />
                <View style={styles.radioTextContent}>
                  <BodyBaseBoldText numberOfLines={0}>
                    {translatedText('withDD.title')}
                  </BodyBaseBoldText>
                  <BodySmText numberOfLines={0}>
                    {translatedText('withDD.description', {
                      minDirectDepositAmount:
                        DD_PROMPT_MIN_DIRECT_DEPOSIT_AMOUNT,
                      maxAdvanceLimit: DD_PROMPT_MAX_ADVANCE_LIMIT,
                    })}
                  </BodySmText>
                </View>
              </View>
              <View style={styles.radioIndicatorContainer}>
                <View style={styles.radioCircle}>
                  {isWithDDSelected ? (
                    <View style={styles.radioInnerCircle} />
                  ) : null}
                </View>
              </View>
            </Pressable>
          ) : null}
          <Pressable
            style={[
              styles.radioCard,
              isWithoutDDSelected
                ? styles.radioCardSelected
                : styles.radioCardUnselected,
            ]}
            onPress={() => setSelectedOption(DirectDepositOption.WITHOUT_DD)}
            accessibilityRole="radio"
            accessibilityState={{ checked: isWithoutDDSelected }}
            accessibilityLabel={translatedText('withoutDD.title')}
          >
            <View style={styles.radioCardContent}>
              <View style={styles.radioTextContent}>
                <BodyBaseBoldText numberOfLines={0}>
                  {translatedText('withoutDD.title')}
                </BodyBaseBoldText>
                <BodySmText numberOfLines={0}>
                  {translatedText('withoutDD.description')}
                </BodySmText>
              </View>
            </View>
            <View style={styles.radioIndicatorContainer}>
              <View style={styles.radioCircle}>
                {isWithoutDDSelected ? (
                  <View style={styles.radioInnerCircle} />
                ) : null}
              </View>
            </View>
          </Pressable>
          <BodyXSText numberOfLines={0} style={styles.disclaimer}>
            {translatedText('disclaimer')}
          </BodyXSText>
        </View>
      </ScrollView>

      <View
        style={[
          styles.buttonContainer,
          { paddingBottom: bottom + theme.spacing.m },
        ]}
      >
        <Button
          label={translatedText('button')}
          onPress={handleGetAdvance}
          type="PRIMARY"
          size="LARGE"
          width="FULL"
          analyticsId={
            isWithDDSelected ? 'ddBorrowMoreWithDD' : 'ddBorrowMoreWithoutDD'
          }
          customAttributes={{
            selected_option: selectedOption,
            cta: isWithDDSelected
              ? 'Continue with direct deposit'
              : 'Continue without direct deposit',
            screen_variant: 'decision_first',
            pinwheel_error: String(!!pinwheelFlowPageError),
          }}
          isDisabled={isPinwheelSdkLoading || pinwheelFlowDecisionLoading}
          isLoading={isPinwheelSdkLoading || pinwheelFlowDecisionLoading}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    borderColor: theme.color.border.primarySolid,
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  disclaimer: {
    color: theme.color.text.secondary,
  },
  headerSection: {
    gap: theme.spacing.s,
    paddingBottom: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  radioCard: {
    borderRadius: 4,
    borderWidth: 1,
    flexDirection: 'row',
    padding: theme.spacing.m,
  },
  radioCardContent: {
    flex: 1,
    gap: theme.spacing.s,
  },
  radioCardSelected: {
    borderColor: theme.color.interactive.primary,
  },
  radioCardUnselected: {
    borderColor: theme.color.border.primarySolid,
  },
  radioCircle: {
    alignItems: 'center',
    borderColor: theme.color.interactive.primary,
    borderRadius: 12,
    borderWidth: 1,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  radioIndicatorContainer: {
    paddingTop: 2,
  },
  radioInnerCircle: {
    backgroundColor: theme.color.interactive.primary,
    borderRadius: 8,
    height: 16,
    width: 16,
  },
  radioTextContent: {
    gap: theme.spacing.s,
  },
  radiosSection: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  scrollContent: {
    flexGrow: 1,
  },
});

export default DDBorrowMore;
